import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import namkeFallback from '../../../assets/namke-fallback.png';
import { useCart } from '../../../contexts/CartContext';

const formatPrice = (p) => {
  const n = typeof p === 'string' ? parseFloat(p) : Number(p);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
};

const PlaceholderImage = () => (
  <img src={namkeFallback} alt="" className="w-full h-full object-cover" />
);

const ProductCard = ({ product: p }) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const origin = [p.origin_country, p.origin_region].filter(Boolean).join(' · ');
  const meta = [p.unit, origin].filter(Boolean).join(' · ');
  const href = p.source === 'dish' ? `/product/${p.id}` : `/grocery-shop-product/${p.id}`;
  const outOfStock = p.stock_quantity != null && p.stock_quantity <= 0;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({
      id: p.id,
      name: p.name,
      price: p.price,
      image: p.image_url,
      restaurantName: p.restaurant_name,
      quantity: 1,
    });
  };

  return (
    <Link
      to={href}
      className={`group bg-namke-white border border-namke-black/10 rounded-[28px] overflow-hidden flex flex-col transition-shadow hover:shadow-[0_16px_32px_rgba(5,16,4,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-namke-terracotta focus-visible:ring-offset-2 ${outOfStock ? 'opacity-60' : ''}`}
      aria-label={t('home.grocery.viewProduct', { name: p.name })}
    >
      <div className="relative w-full aspect-square bg-namke-cream overflow-hidden flex-shrink-0">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <PlaceholderImage />
        )}
        {outOfStock && (
          <span className="absolute top-3 left-3 bg-namke-black/70 text-namke-white text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full">
            {t('home.grocery.outOfStock')}
          </span>
        )}
        {!outOfStock && p.is_african && (
          <span className="absolute top-3 left-3 bg-namke-terracotta text-namke-white text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full">
            {t('home.grocery.african')}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <h3 className="font-display font-semibold text-[17px] leading-tight text-namke-black line-clamp-2">
          {p.name}
        </h3>
        {(p.restaurant_name || p.grocery_shop_name) && (
          <p className="text-[12.5px] text-namke-black/64">
            {p.restaurant_name || p.grocery_shop_name}
          </p>
        )}
        {meta && <p className="text-[12.5px] text-namke-black/64 line-clamp-1">{meta}</p>}

        <div className="flex items-center gap-2.5 mt-auto pt-2.5">
          <span className="font-semibold text-[16.5px] text-namke-black">{formatPrice(p.price)}</span>
          {!outOfStock && (
            <button
              type="button"
              onClick={handleQuickAdd}
              aria-label={t('home.grocery.addProduct', { name: p.name })}
              className="ml-auto w-[38px] h-[38px] rounded-full bg-namke-terracotta text-namke-white flex items-center justify-center hover:bg-secondary-600 transition-colors"
            >
              <Plus size={17} strokeWidth={2.75} />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

const GroceryShopSection = ({
  products,
  loading,
  totalCount,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-namke-cream border-t-[rgb(5,70,37)]" />
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <p className="text-namke-black/60 text-center py-12">{t('home.grocery.noProducts')}</p>
    );
  }

  const count = typeof totalCount === 'number' ? totalCount : products.length;

  return (
    <div className="mb-8">
      <p className="text-sm text-namke-black/55 mb-4">{t('home.grocery.productCount', { count })}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
        {products.map((p) => (
          <ProductCard key={`${p.source || 'catalog'}-${p.id}`} product={p} />
        ))}
      </div>

      {!loading && totalPages > 1 && typeof onPageChange === 'function' && (
        <div className="flex justify-center items-center mt-8 mb-4">
          <div className="flex items-center gap-2 bg-namke-white rounded-full border border-namke-black/10 p-2">
            <span className="text-sm text-namke-black/60 px-2">{t('home.dishes.pageOf', { current: currentPage, total: totalPages })}</span>
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-4 py-2 rounded-full text-sm font-medium text-namke-black/75 hover:bg-namke-cream disabled:text-namke-black/25 disabled:cursor-not-allowed transition-colors"
            >
              {t('home.previous')}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-4 py-2 rounded-full text-sm font-medium text-namke-black/75 hover:bg-namke-cream disabled:text-namke-black/25 disabled:cursor-not-allowed transition-colors"
            >
              {t('home.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroceryShopSection;
